// GatheringMatcher.js
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import ProfileForm, { steps } from "./ProfileForm";
import HelpFeedback from "./HelpFeedback";
import useNoIndex from "../hooks/useNoIndex";
import { trackPageView, trackFunnelStep, trackEvent, setUserIdProperty } from "../utils/tracking";
import { handleAuthError, ExpiredSessionModal } from "../utils/authHelpers";

const GatheringMatcher = () => {
  const navigate = useNavigate();

  // Prevent search engines from indexing this member-only page
  useNoIndex();

  const [isExistingUser, setIsExistingUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [showExpiredSessionModal, setShowExpiredSessionModal] = useState(false);

  // Determine which steps to show based on theme type and user status
  const [visibleSteps, setVisibleSteps] = useState([]);
  const [signupType, setSignupType] = useState("casual");

  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = visibleSteps.length;
  const isLoggedIn = !!localStorage.getItem("memberstackToken");

  // Track page view on mount
  useEffect(() => {
    setUserIdProperty(); // Set user ID if logged in
    const themeId = sessionStorage.getItem('selectedThemeId');
    const themeName = sessionStorage.getItem('selectedThemeName');
    const themeType = sessionStorage.getItem('selectedThemeType');
    const hasReferralCode = localStorage.getItem('referralCode');

    trackPageView('Gathering Matcher', {
      theme_id: themeId,
      theme_name: themeName,
      theme_type: themeType,
      signup_type: signupType,
      total_steps: visibleSteps.length,
      has_referral_code: !!hasReferralCode,
      referral_code: hasReferralCode || null
    });

    // Track referral funnel step if user came from referral
    if (hasReferralCode && mixpanel && mixpanel.__loaded) {
      mixpanel.track('Referral Funnel: Gathering Matcher Viewed', {
        funnel_step: 'gathering_matcher',
        funnel_step_number: 3,
        referral_code: hasReferralCode,
        theme_id: themeId,
        theme_name: themeName,
        theme_type: themeType
      });
    }
  }, [signupType, visibleSteps.length]);

  // Determine which steps to show based on theme type and profile completion status
  useEffect(() => {
    const determineVisibleSteps = () => {
      const themeType = sessionStorage.getItem('selectedThemeType');
      const token = localStorage.getItem("memberstackToken");

      // Show name then location for new users
      let stepsToShow = [steps.basicInfo5, steps.location];
      let type = "casual";

      // Check if user is logged in with partial profile
      if (token && userProfile) {
        const onlyNetworking = userProfile.onlyNetworkingProfile;

        if (onlyNetworking === 0 || onlyNetworking === 1) {
          // Profile already exists (complete or networking-only) - redirect to slots
          navigate('/slots');
          return;
        }
      }
      // New user on networking theme - show name then location
      else if (!token && themeType === 'professional') {
        stepsToShow = [steps.basicInfo5, steps.location];
        type = "networking";
      }

      console.log('[GatheringMatcher] determineVisibleSteps:', {
        themeType,
        hasToken: !!token,
        hasUserProfile: !!userProfile,
        onlyNetworkingProfile: userProfile?.onlyNetworkingProfile,
        signupType: type,
        stepsCount: stepsToShow.length,
        firstStep: stepsToShow[0]
      });

      setVisibleSteps(stepsToShow);
      setSignupType(type);

      // Store signup type for use in CreateAccount
      localStorage.setItem("signupType", type);
    };

    determineVisibleSteps();
  }, [userProfile, navigate]);

  // Check if user is already logged in and has existing profile
  useEffect(() => {
    const checkExistingUser = async () => {
      try {
        const token = localStorage.getItem("memberstackToken");

        if (!token) {
          // No token = not logged in, proceed with normal signup flow
          setIsLoading(false);
          return;
        }

        // User is logged in, check their current profile
        // Use fetch directly so we can check response status for 401
        const response = await fetch('/api/standard-user/getProfile', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Check for 401 - expired/invalid token
        if (handleAuthError(response, setShowExpiredSessionModal)) {
          setIsLoading(false);
          return; // Stop execution, modal will handle the UX
        }

        if (response.ok) {
          const profile = await response.json();
          setUserProfile(profile);

          console.log('[GatheringMatcher] checkExistingUser:', {
            onlyNetworkingProfile: profile.onlyNetworkingProfile,
            selectedThemeType: sessionStorage.getItem('selectedThemeType')
          });

          // Any existing profile (complete or networking-only) goes straight to slots
          if (profile.onlyNetworkingProfile === 0 || profile.onlyNetworkingProfile === 1) {
            navigate('/slots');
            return;
          }
        }

        // If we get here, proceed with normal flow
        setIsLoading(false);

      } catch (error) {
        console.error("Error checking existing user:", error);
        setIsLoading(false);
      }
    };

    checkExistingUser();
  }, [navigate]);

  // Reference to ProfileForm
  const profileFormRef = useRef(null);

  // Called when the wizard is complete. Brand-new visitors go on to create
  // an account. Already-logged-in members must NOT be sent to create-account
  // (their email is already registered there, which fails with "email
  // already in use" and forces them to log in again) — instead save the
  // profile directly and continue on to booking.
  const handleFormComplete = async () => {
    const token = localStorage.getItem("memberstackToken");

    if (!token) {
      navigate("/create-account");
      return;
    }

    try {
      const payload = JSON.parse(localStorage.getItem("profileData") || "{}");

      const response = await fetch('/api/standard-user/updateProfile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (handleAuthError(response, setShowExpiredSessionModal)) {
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to update profile: ${response.status}`);
      }

      navigate('/slots');
    } catch (error) {
      console.error("Error saving profile for logged-in user:", error);
      alert("Failed to save your profile. Please try again.");
    }
  };

  // Utility: Save the current data from the form into localStorage
  const saveCurrentData = () => {
    if (profileFormRef.current) {
      const payload = profileFormRef.current.getFormPayload();
      payload.signupType = signupType; // Include signup type in payload
      localStorage.setItem("profileData", JSON.stringify(payload));
    }
  };

  const nextStep = () => {
    // Check if we're on the intro step (-1)
    const actualStep = visibleSteps ? visibleSteps[currentStep] : currentStep;
    const isIntroStep = actualStep === -1;

    // 1) Save current data (skip if intro step)
    if (!isIntroStep) {
      saveCurrentData();
    }

    // 2) Validate required fields for the current step (skip if intro step)
    if (!isIntroStep && profileFormRef.current) {
      const isValid = profileFormRef.current.validateRequiredFieldsForStep();
      if (!isValid) {
        return;
      }
    }

    // Track step progression
    trackFunnelStep(
      'Profile Creation',
      visibleSteps[currentStep]?.id || `step_${currentStep}`,
      currentStep + 1,
      {
        total_steps: totalSteps,
        signup_type: signupType,
        is_moving_forward: true
      }
    );

    // 3) Advance to next step
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }

    // Remove focus from the button to reset its styling
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
  };

  const backStep = () => {
    // Check if we're on the intro step (-1)
    const actualStep = visibleSteps ? visibleSteps[currentStep] : currentStep;
    const isIntroStep = actualStep === -1;

    // 1) Save current data (skip if intro step)
    if (!isIntroStep) {
      saveCurrentData();
    }

    // 2) Go back to previous step
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }

    // Remove focus from the button to reset its styling
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
  };

  const isLastStep = currentStep === totalSteps - 1;

  // On last step's "Save," we call the form's submit
  const handleSaveClick = () => {
    // Final store in localStorage
    saveCurrentData();

    // Validate required text/dropdown fields for the current step
    if (profileFormRef.current) {
      const isValid = profileFormRef.current.validateRequiredFieldsForStep();
      if (!isValid) {
        return;
      }

      // Track completion of profile form
      trackEvent('Profile Creation: Completed', {
        signup_type: signupType,
        total_steps: totalSteps,
        theme_id: sessionStorage.getItem('selectedThemeId'),
        theme_name: sessionStorage.getItem('selectedThemeName')
      });

      // If valid, actually call the form's submit handler
      profileFormRef.current.submitForm();
    }
  };

  // Show loading state while checking user status or determining visible steps
  if (isLoading || visibleSteps.length === 0) {
    return (
      <div className="page-wrapper">
        <main className="main-wrapper">
          <section className="section_hero">
            <div className="padding-global padding-section-large container-large">
              <div className="hero_card is-vertical" style={{ position: "relative" }}>
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <p>Setting up your profile...</p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="page-wrapper" style={{ backgroundColor: "#FAFAFA", minHeight: "100vh" }}>
      <main className="main-wrapper">
        <section className="section_hero">
          <div className="padding-global padding-section-large container-large">
            <div className="hero_card is-vertical" style={{
              position: "relative",
              backgroundColor: "#fff",
              borderRadius: "20px",
              padding: "40px",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
              border: "2px solid rgba(139, 95, 191, 0.1)",
              maxWidth: "800px",
              margin: "0 auto"
            }}>
              <HelpFeedback />
              {!isLoggedIn && (
                <a href="/login"
                  style={{
                    position: "absolute",
                    top: "10px",
                    left: "10px",
                    background: "linear-gradient(135deg, #8B5FBF, #2c2e96)",
                    color: "#fff",
                    padding: "12px 24px",
                    borderRadius: "25px",
                    textDecoration: "none",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 10px rgba(139, 95, 191, 0.2), 0 1px 4px rgba(139, 95, 191, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                    border: "none",
                    zIndex: 1100,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "linear-gradient(135deg, #9D6FD1, #8B5FBF)";
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 4px 15px rgba(139, 95, 191, 0.3), 0 2px 6px rgba(139, 95, 191, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "linear-gradient(135deg, #8B5FBF, #2c2e96)";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 2px 10px rgba(139, 95, 191, 0.2), 0 1px 4px rgba(139, 95, 191, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                  }}
                > Login
                </a>
              )}

              <div style={{
                textAlign: "center",
                marginBottom: "20px",
              }}>
                <h4>
                  Create Your Account
                </h4>

                {currentStep === 0 && (
                  <p style={{
                    fontSize: "0.95rem",
                    color: "#666",
                    marginTop: "8px",
                    marginBottom: "0px",
                    maxWidth: "500px",
                    margin: "8px auto 0px auto",
                    lineHeight: "1.4",
                    textAlign: "center"
                  }}>
                    Step 1 of 2 — Just your name to get started.
                  </p>
                )}
                {currentStep === 1 && (
                  <p style={{
                    fontSize: "0.95rem",
                    color: "#666",
                    marginTop: "8px",
                    marginBottom: "0px",
                    maxWidth: "500px",
                    margin: "8px auto 0px auto",
                    lineHeight: "1.4",
                    textAlign: "center"
                  }}>
                    Step 2 of 2 — Where would you like to attend gatherings?
                  </p>
                )}
              </div>

              {(
                <ProfileForm
                  ref={profileFormRef}
                  headingText=""
                  onSubmitSuccess={handleFormComplete}
                  multiStep={true}
                  currentStep={currentStep}
                  visibleSteps={visibleSteps}
                  signupType={signupType}
                  showSubmitButton={false} // We'll control the final Save button here
                />
              )}

              <div style={{ marginTop: "1rem", textAlign: "center", display: "flex", gap: "12px", justifyContent: "center", flexWrap: "nowrap" }}>
                {currentStep > 0 && (
                  <button
                    onClick={backStep}
                    className="button-2 is-black w-button"
                    style={{
                      background: "transparent",
                      color: "#8B5FBF",
                      padding: "14px 32px",
                      borderRadius: "25px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      transition: "all 0.3s ease",
                      border: "2px solid #8B5FBF",
                      cursor: "pointer",
                      minWidth: "120px"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#f5f2ff";
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow = "0 4px 12px rgba(139, 95, 191, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    Back
                  </button>
                )}

                {!isLastStep && (
                  <button
                    onClick={nextStep}
                    className="button-2 is-black w-button"
                    style={{
                      background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                      color: "#fff",
                      padding: "14px 32px",
                      borderRadius: "25px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      transition: "all 0.3s ease",
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0 4px 15px rgba(139, 95, 191, 0.3)",
                      minWidth: "120px"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #9D6FD1, #8B5FBF)";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 6px 20px rgba(139, 95, 191, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #8B5FBF, #6B46C1)";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 4px 15px rgba(139, 95, 191, 0.3)";
                    }}
                  >
                    Continue
                  </button>
                )}
                {isLastStep && (
                  <button
                    onClick={handleSaveClick}
                    className="button-2 is-black w-button"
                    style={{
                      background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                      color: "#fff",
                      padding: "14px 32px",
                      borderRadius: "25px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      transition: "all 0.3s ease",
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0 4px 15px rgba(139, 95, 191, 0.3)",
                      minWidth: "120px"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #9D6FD1, #8B5FBF)";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 6px 20px rgba(139, 95, 191, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #8B5FBF, #6B46C1)";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 4px 15px rgba(139, 95, 191, 0.3)";
                    }}
                  >
                    Confirm
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Expired Session Modal */}
      <ExpiredSessionModal
        show={showExpiredSessionModal}
        onLogin={() => navigate("/login")}
        onCreateAccount={() => navigate("/gathering-matcher")}
      />
    </div>
  );
};

export default GatheringMatcher;
